import type { Database, Json } from '@/integrations/supabase/types';
import { BaseService } from '@/services/base/BaseService';

type JobRow = Database['public']['Tables']['reach_jobs']['Row'];
type JobStepRow = Database['public']['Tables']['reach_job_steps']['Row'];

/** Verified against `reach_jobs_job_type_check`. */
export type ReachJobType =
  | 'import_preview'
  | 'import_commit'
  | 'initial_triage'
  | 'provider_enrichment'
  | 'web_research'
  | 'assessment'
  | 'briefing'
  | 'knowledge_ingestion'
  | 'retention_purge';

/**
 * Verified against `reach_jobs_status_check`.
 *
 * `partial` is a REAL terminal status, not a degraded `succeeded`: the
 * finalization trigger (`reach_jobs_finalization_gate`) refuses `succeeded`
 * while any step is non-terminal, so a run with a failed or skipped step ends
 * here. Never collapse it into `succeeded` on the way to the UI.
 */
export type ReachJobStatus =
  | 'queued'
  | 'running'
  | 'awaiting_approval'
  | 'partial'
  | 'succeeded'
  | 'failed'
  | 'cancel_requested'
  | 'cancelled';

/** Verified against `reach_job_steps_status_check`. */
export type ReachJobStepStatus =
  'pending' | 'running' | 'succeeded' | 'skipped' | 'failed' | 'cancelled';

/** Verified against `reach_job_steps_finding_kind_check`. */
export type ReachJobFindingKind = 'found' | 'none' | 'partial';

/**
 * The statuses that mean "still going". Mirrors the partial index
 * `reach_jobs_active_idx`, which is the schema's own definition of active.
 */
export const REACH_JOB_ACTIVE_STATUSES: readonly ReachJobStatus[] = [
  'queued',
  'running',
  'awaiting_approval',
  'cancel_requested',
];

export function isReachJobActive(status: string): boolean {
  return (REACH_JOB_ACTIVE_STATUSES as readonly string[]).includes(status);
}

export function isReachJobTerminal(status: string): boolean {
  return !isReachJobActive(status);
}

export interface ReachJobStep {
  id: string;
  jobId: string;
  position: number;
  stepKey: string;
  label: string;
  status: ReachJobStepStatus;
  /** The sentence the user reads. Null only while the step is non-terminal. */
  finding: string | null;
  findingKind: ReachJobFindingKind | null;
  counts: Json | null;
  detail: Json | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface ReachJob {
  id: string;
  workspaceId: string;
  eventId: string | null;
  ingestBatchId: string | null;
  jobType: ReachJobType;
  status: ReachJobStatus;
  workflowRunId: string | null;
  progressCurrent: number;
  progressTotal: number | null;
  currentStage: string | null;
  costEstimate: Json | null;
  costActual: Json | null;
  errorCode: string | null;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  steps: ReachJobStep[];
}

function mapStep(row: JobStepRow): ReachJobStep {
  return {
    id: row.id,
    jobId: row.job_id,
    position: row.position,
    stepKey: row.step_key,
    label: row.label,
    status: row.status as ReachJobStepStatus,
    finding: row.finding,
    findingKind: row.finding_kind as ReachJobFindingKind | null,
    counts: row.counts,
    detail: row.detail,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    createdAt: row.created_at,
  };
}

function mapJob(row: JobRow, steps: ReachJobStep[]): ReachJob {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    eventId: row.event_id,
    ingestBatchId: row.ingest_batch_id,
    jobType: row.job_type as ReachJobType,
    status: row.status as ReachJobStatus,
    workflowRunId: row.workflow_run_id,
    progressCurrent: row.progress_current,
    progressTotal: row.progress_total,
    currentStage: row.current_stage,
    costEstimate: row.cost_estimate,
    costActual: row.cost_actual,
    errorCode: row.error_code,
    errorMessage: row.error_message,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    steps,
  };
}

const DEFAULT_LIMIT = 10;

/**
 * `reach_jobs` / `reach_job_steps` — the receipt for a durable Reach
 * workflow. Written since day one and, until Round 2, surfaced nowhere.
 *
 * Scoped exactly like every other Reach service: `workspace_id` on the query,
 * `owner_id` enforced by RLS. `reach_jobs` is account-scoped in
 * `table-scope.generated.ts`, so the raw JWT id is not the tenant here.
 */
export class ReachJobService extends BaseService {
  async listForEvent(
    eventId: string,
    workspaceId: string,
    limit: number = DEFAULT_LIMIT,
  ): Promise<ReachJob[]> {
    const { data, error } = await this.client
      .from('reach_jobs')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('event_id', eventId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;

    const jobs = (data ?? []) as JobRow[];
    if (jobs.length === 0) return [];

    const stepsByJob = await this.loadSteps(
      jobs.map((job) => job.id),
      workspaceId,
    );
    return jobs.map((job) => mapJob(job, stepsByJob.get(job.id) ?? []));
  }

  async getLatestForEvent(
    eventId: string,
    workspaceId: string,
    jobType?: ReachJobType,
  ): Promise<ReachJob | null> {
    let query = this.client
      .from('reach_jobs')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('event_id', eventId);
    if (jobType) query = query.eq('job_type', jobType);

    const { data, error } = await query.order('created_at', { ascending: false }).limit(1);
    if (error) throw error;

    const jobs = (data ?? []) as JobRow[];
    if (jobs.length === 0) return null;

    const stepsByJob = await this.loadSteps([jobs[0].id], workspaceId);
    return mapJob(jobs[0], stepsByJob.get(jobs[0].id) ?? []);
  }

  private async loadSteps(
    jobIds: string[],
    workspaceId: string,
  ): Promise<Map<string, ReachJobStep[]>> {
    const byJob = new Map<string, ReachJobStep[]>();
    if (jobIds.length === 0) return byJob;

    const { data, error } = await this.client
      .from('reach_job_steps')
      .select('*')
      .eq('workspace_id', workspaceId)
      .in('job_id', jobIds)
      .order('position', { ascending: true });
    if (error) throw error;

    for (const row of (data ?? []) as JobStepRow[]) {
      const list = byJob.get(row.job_id) ?? [];
      list.push(mapStep(row));
      byJob.set(row.job_id, list);
    }
    return byJob;
  }
}

export const reachJobService = new ReachJobService();
